import { DataBaseNodes } from '@lib/firebase';
import { usePatientStore, useSiteStore } from '@stores';
import {
  Medication,
  MedicationPlan,
  SourceUnsubscribe,
  UUIDTypes,
} from '@type';
import {
  extractMedicationsFromReminder,
  formatDate,
  getDateNow,
  getDateUTCOffset,
  isMedicationPlan,
} from '@utils';

import { Encryption } from '../../encryption-service';
import {
  CommunicationKeys,
  Preferences,
  ReminderNodeValue,
  ReminderResponse,
} from '../types';
import { MedicationPlanAssetEncryptedResponse } from '../types/medication-gateway.types';
import { BaseGateway } from './base.gateway';

export class MedicationGateway extends BaseGateway {
  answerMedication = async (
    medication: Medication,
    isPreviouslyChecked: boolean
  ) => {
    const { sessionId, userId, uuid } = usePatientStore.getState().patientInfo;
    const timeZoneOffset = getDateUTCOffset();
    const data = {
      preferece: Preferences.Reminders.AnswerMedications,
      patientId: medication.patientId,
      timestamp: formatDate(getDateNow()),
      timeZoneOffset,
      encrypted: false,
      type: 'reminderResponse',
      reminderType: 'reminderResponse',
      reminderId: medication.reminderId,
      consent: null,
      occurrenceId: medication.occurrence.id,
      response: isPreviouslyChecked ? 0 : 1,
      user: { id: userId },
      uuid,
      uuidType: UUIDTypes.PatientUuidType,
      answeredBy: null,
      userSessionId: sessionId,
    };
    const encryptedData = Encryption.encrypt(
      data,
      medication.site.sharedSecretKey
    );
    const payload = {
      encrypted: true,
      message: encryptedData,
      utcOffset: timeZoneOffset,
      hashedSharedKey: medication.site.hashedSharedKey,
    };
    return this.dataExchange({
      siteUuid: medication.site.siteUuid,
      payload,
      query: DataBaseNodes.Responses,
      successKey: CommunicationKeys.Answer.Success,
      failureKey: CommunicationKeys.Answer.Failed,
      pushWithKey: false,
    });
  };

  sendMedicationPlan = async (medicationPlan: MedicationPlan[]) => {
    const getSiteByProjectId = useSiteStore.getState().getSiteByProjectId;
    const site = getSiteByProjectId(this.config.projectId as string);

    const rawPlan = medicationPlan.map(plan => plan.xmlData);
    const encryptedPlan = Encryption.encrypt(rawPlan, site.sharedSecretKey);

    return this.dataExchange({
      siteUuid: site.siteUuid,
      payload: {
        firebasePatientId: this.config.patientSourceUUID,
        preference: Preferences.Reminders.SendMedicationPlan,
        hashedSharedKey: site.hashedSharedKey,
        bmp: encryptedPlan,
        pushWithKey: true,
        query: DataBaseNodes.Request,
      },
      successKey: CommunicationKeys.MedicationPlan.Success,
      failureKey: [
        CommunicationKeys.MedicationPlan.Failure,
        CommunicationKeys.MedicationPlan.NotSupported,
        CommunicationKeys.MedicationPlan.Duplicated,
      ],
    });
  };

  archiveMedicationPlan = async () => {
    const getSiteByProjectId = useSiteStore.getState().getSiteByProjectId;
    const { siteUuid } = getSiteByProjectId(this.config.projectId as string);

    return this.dataExchange({
      siteUuid,
      payload: {
        preference: Preferences.Reminders.ArchiveMedicationPlan,
        firebasePatientId: this.config.patientSourceUUID,
      },
      successKey: CommunicationKeys.MedicationPlan.ArchiveSuccess,
      failureKey: CommunicationKeys.MedicationPlan.ArchivingFailed,
    });
  };

  getActiveMedicationPlanAsset = async () => {
    const getSiteByProjectId = useSiteStore.getState().getSiteByProjectId;
    const { siteUuid } = getSiteByProjectId(this.config.projectId as string);

    return await this.dataExchange<MedicationPlanAssetEncryptedResponse>({
      siteUuid,
      payload: {
        preference: Preferences.Reminders.FetchMedicationPlanAsset,
        firebasePatientId: this.config.patientSourceUUID,
      },
      successKey:
        CommunicationKeys.MedicationPlan.FetchMedicationPlanAssetSuccess,
      failureKey: [
        CommunicationKeys.MedicationPlan.FetchMedicationPlanAssetFailed,
        CommunicationKeys.MedicationPlan.MedicationPlanAssetNotFound,
      ],
    });
  };

  subscribeToMedications = (
    callback: (medications: Medication[], isMedicationPlan: boolean) => unknown
  ): SourceUnsubscribe => {
    return this.sourceService.subscribeToChild(
      `${DataBaseNodes.Patients}/${this.config.patientSourceUUID}/${DataBaseNodes.Reminders}`,
      snapshot => {
        if (!snapshot.exists()) return;
        const { primarySourceId } = usePatientStore.getState().patientInfo;
        const { getSiteByUUID } = useSiteStore.getState();
        const reminders = snapshot.val() as {
          [key: string]: ReminderNodeValue;
        };
        let isBMP = false;
        const medications: Medication[] = [];
        for (const reminder of Object.values(reminders)) {
          const { document, siteUUID, validOccurrenceToday } = reminder;
          if (!siteUUID || !validOccurrenceToday) continue;

          const site = getSiteByUUID(siteUUID);
          const decrypedReminder =
            Encryption.decryptAndParseObject<ReminderResponse>(
              document,
              site.sharedSecretKey
            );
          if (!decrypedReminder?.medication) continue;
          decrypedReminder.allOccurrences = validOccurrenceToday;
          const newMedications = extractMedicationsFromReminder(
            decrypedReminder,
            site
          );
          if (isMedicationPlan(newMedications)) {
            if (this.config.projectId !== primarySourceId) continue;
            isBMP = true;
          }
          medications.push(...newMedications);
        }
        callback(medications, isBMP);
      }
    );
  };

  subscribeToMedicationsRemoved = (
    callback: (medications: Medication[]) => unknown
  ): SourceUnsubscribe => {
    return this.sourceService.subscribeToChildRemoved(
      `${DataBaseNodes.Patients}/${this.config.patientSourceUUID}/${DataBaseNodes.Reminders}`,
      snapshot => {
        if (!snapshot.exists()) return;
        const { getSiteByUUID } = useSiteStore.getState();
        const { document, siteUUID } = snapshot.val() as ReminderNodeValue;

        if (!siteUUID) return;

        const site = getSiteByUUID(siteUUID);
        const reminder = Encryption.decryptAndParseObject<ReminderResponse>(
          document,
          site.sharedSecretKey
        );
        if (!reminder?.medication) return;
        const medications = extractMedicationsFromReminder(reminder, site);
        callback(medications);
      }
    );
  };
}
